import { z } from 'zod';
import { eventNameSchema } from './event';

// ALLOCATION — the experiment half of a campaign (ADR 0015). This is a CAMPAIGN field, not an
// experience field: the experience owns the variant CONTENT (authoring doc v3), the campaign owns
// who-sees-which-arm, the same split ADR 0009 draws for every other delivery question. It never
// reaches a device: qualify() resolves it server-side and ships one flow (ADR 0008's
// confidentiality invariant, extended from `segment_rule` to weights and sibling arms).

/** Stable, human-set experiment identity (ADR 0016). Environment-portable where row ids are not:
 *  a staging campaign and its production promotion carry the same key, so analytics see one
 *  experiment. Same charset contract as a placement slot — a human copies this into a BI tool. */
export const experimentKeySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9_]+$/, 'an experiment key is lowercase letters, digits and underscores');

// (`DEFAULT_VARIANT_KEY` — the implicit control arm's name — lives in `authoring.ts`, next to the
// doc shape that makes it true.)

export const allocationArmSchema = z.strictObject({
  /** `'default'` or a variant key of the linked experience. Publish verifies it resolves. */
  variant: z.string().min(1).max(64),
  /** BASIS POINTS (10_000 = 100%). Integers, so the weight-sum law is exact — floats would make
   *  "must total 100%" a tolerance question. 0 is legal: a paused arm keeps its bucket range, so
   *  reviving it does not reshuffle every other arm's users. */
  weight: z.number().int().min(0).max(10_000),
});
export type AllocationArm = z.infer<typeof allocationArmSchema>;

export const allocationSchema = z
  .strictObject({
    experiment_key: experimentKeySchema,
    // ponytail: 8 is a DoS bound, not a product opinion — same posture as steps' 20.
    arms: z.array(allocationArmSchema).min(2).max(8),
    /** Users who see NOTHING — the derivable control group and the staged-rollout dial. They are
     *  excluded from the qualified set entirely; their assignment stays recomputable from the pure
     *  hash, so "holdout vs exposed" is a query, not a table. */
    holdout_weight: z.number().int().min(0).max(9_999).default(0),
  })
  .superRefine((allocation, ctx) => {
    const total = allocation.holdout_weight + allocation.arms.reduce((sum, arm) => sum + arm.weight, 0);
    if (total !== 10_000) {
      ctx.addIssue({
        code: 'custom',
        path: ['arms'],
        message: `arm weights + holdout must total 10000 basis points, got ${total}`,
      });
    }
    const seen = new Set<string>();
    allocation.arms.forEach((arm, index) => {
      if (seen.has(arm.variant)) {
        ctx.addIssue({
          code: 'custom',
          path: ['arms', index, 'variant'],
          message: `arm "${arm.variant}" appears twice — one bucket range per variant`,
        });
      }
      seen.add(arm.variant);
    });
  });
export type Allocation = z.infer<typeof allocationSchema>;

/** ADR 0009 §7's "optional measurement goal", as a field. The SEMANTICS are ADR 0016's: a user's
 *  conversion is their first `event` within `window_days` after their first exposure. Counting
 *  lands with the event-history store (roadmap V3); the definition is pinned now so no customer
 *  ever compares numbers computed two different ways. */
export const campaignGoalSchema = z.strictObject({
  event: eventNameSchema,
  window_days: z.number().int().positive().max(90).default(7),
});
export type CampaignGoal = z.infer<typeof campaignGoalSchema>;

// ---------------------------------------------------------------------------- assignment

/** TOTAL_BUCKETS — the resolution of a weight. Matches the basis-point unit above by construction. */
export const TOTAL_BUCKETS = 10_000;

/** FNV-1a 32-bit → bucket. THIS FUNCTION IS PINNED THE MOMENT A REAL USER IS BUCKETED BY IT —
 *  changing it reassigns every in-flight experiment's users, which is why `allocation.test.ts`
 *  golden-pins its outputs the way `rules.golden.json` pins the evaluator. Deterministic on
 *  (unit, experiment): the same user lands in the same bucket on every qualify, which IS the
 *  stickiness story (no assignment table, no cookie, nothing a client could forge — the exact
 *  opposite of Plasmic's unsigned `plasmic:exp.*` cookie). Keyed on the experiment so one user
 *  lands in DIFFERENT buckets across experiments — reusing a seed correlates every test a
 *  customer runs. */
export function bucketOf(unitId: string, experimentKey: string): number {
  const s = `${experimentKey}:${unitId}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % TOTAL_BUCKETS;
}

export type ArmAssignment = { kind: 'arm'; variant: string } | { kind: 'holdout' };

/** Walk the bucket ranges: [0, holdout) is the holdout, then each arm in ARRAY ORDER — order is
 *  part of the contract (reordering arms reassigns users; the dashboard edits weights in place).
 *  The final return is unreachable while the weight-sum law holds; it exists so a malformed
 *  allocation that slipped every gate still yields a lawful answer instead of Plasmic's silent
 *  bucket-0. Falling to the LAST arm (not the first) makes the failure visible in results rather
 *  than inflating control. */
export function assignArm(unitId: string, allocation: Allocation): ArmAssignment {
  const bucket = bucketOf(unitId, allocation.experiment_key);
  if (bucket < allocation.holdout_weight) return { kind: 'holdout' };
  let upper = allocation.holdout_weight;
  for (const arm of allocation.arms) {
    upper += arm.weight;
    if (bucket < upper) return { kind: 'arm', variant: arm.variant };
  }
  return { kind: 'arm', variant: allocation.arms[allocation.arms.length - 1]!.variant };
}
